import type { FeatureKey, Features } from './types'

/**
 * Paketler (29 Eki 2026): Start, Plus, Max. Fiyatlar ve maddeler tanıtım
 * sitesindeki listeyle aynı (site-v2/src/content.js, www.adissoo.com/fiyatlar).
 *
 * Paket bir özellik listesi: kuruma paket seçilince (Sistem Yönetimi) o
 * paketin özellikleri açılır. Paket dışındaki özellikler (görselli menü,
 * yemek kartı) ek modül, kuruma tek tek açılır ve paket değişince korunur.
 *
 * supabase/functions/activate ve staff'taki kopyayla (PLAN_FEATURES, personel
 * sınırı) ve supabase/paketler-v2.sql'deki plan_staff_limit ile aynı olmalı.
 */
export type PlanId = 'start' | 'plus' | 'max'
export type PlanPeriod = 'aylik' | 'yillik'

export interface Plan {
  id: PlanId
  name: string
  /** Aylık, KDV hariç, TL. */
  monthly: number
  /** Yıllık peşin, KDV hariç, TL. */
  yearly: number
  /** Kasiyer ve garson sayısı; patron ve mutfak ekranı sayılmaz. null: sınırsız. */
  staff: number | null
  /** Bu paketle açılan özellikler (öncekilerin hepsi dahil). */
  features: FeatureKey[]
}

// 30 Eki: QR menü Start'tan Plus'a geçti; salon atama paket özelliği değil,
// herkese açık (kullanıcı: "gözükmesin"). Üsküdar Börekçisi Start ama QR'ı
// açık kalıyor (istisna): paket yeniden seçilirse QR elle açılmalı.
const START: FeatureKey[] = ['sadaka', 'tarti']
const PLUS: FeatureKey[] = [...START, 'qr', 'mutfak', 'stok', 'cari']
const MAX: FeatureKey[] = [...PLUS, 'maliyet', 'takip']

export const PLANS: Plan[] = [
  { id: 'start', name: 'Start', monthly: 1190, yearly: 11900, staff: 3, features: START },
  { id: 'plus', name: 'Plus', monthly: 1750, yearly: 17500, staff: 7, features: PLUS },
  { id: 'max', name: 'Max', monthly: 2450, yearly: 24500, staff: null, features: MAX },
]

/** Paketlerde olmayan, tek tek açılan özellikler; paket değişince korunur. */
export const EXTRA_FEATURES: FeatureKey[] = ['gorsel', 'yemek', 'kiosk', 'qrsiparis', 'damga']

/**
 * Ek modüllerin aylık fiyatı, TL (site-v2/src/content.js ADDONS ile aynı).
 * Abonelikler'de liste fiyatı bunlarla hesaplanıyor; fiyatı olmayan ek
 * modül (QR Sipariş, yemek kartı) 0 sayılıyor.
 */
export const ADDON_MONTHLY: Partial<Record<FeatureKey, number>> = { gorsel: 200, damga: 390, kiosk: 490 }

export const PERIOD_LABEL: Record<PlanPeriod, string> = { aylik: 'Aylık', yillik: 'Yıllık' }

export const PRICES_URL = 'https://www.adissoo.com/fiyatlar'

export const planOf = (id: string | null | undefined): Plan | null => PLANS.find((p) => p.id === id) ?? null

/** Özelliği açan en küçük paket; paket dışı (ek modül) özellikte null. */
export const planFor = (key: FeatureKey): Plan | null => PLANS.find((p) => p.features.includes(key)) ?? null

/**
 * Paket seçilince kurumun özellikleri: paketinkiler açık, öteki paket
 * özellikleri kapalı, ek modüller olduğu gibi.
 */
export function featuresFor(plan: PlanId, current: Features): Features {
  const next: Features = {}
  for (const key of EXTRA_FEATURES) if (current[key]) next[key] = true
  for (const key of planOf(plan)!.features) next[key] = true
  return next
}

export const formatPrice = (tl: number) => `₺${tl.toLocaleString('tr-TR')}`
